import { AppBar, Avatar, Card, CardContent, Chip, Divider, Grid, List, ListItem, ListItemAvatar, ListItemText, Toolbar, Typography } from '@material-ui/core';
import blueGrey from '@material-ui/core/colors/blueGrey';
import { createTheme, makeStyles, ThemeProvider } from '@material-ui/core/styles';
import EventAvailableIcon from '@material-ui/icons/EventAvailable';
import FaceIcon from '@material-ui/icons/Face';
import RecentActorsIcon from '@material-ui/icons/RecentActors';
import numberToText from 'number2text';
import QRCode from 'qrcode';
import React from 'react';

/**
 * Shareable card image for an anumati record.
 *
 * This mirrors the detailed sandesh tile in the sister app so a card shared from here is
 * identical to one shared from there.
 *
 * The sister app sets its root font to 10px and scales Material typography against that.
 * Those sizes are pinned here in pixels instead, so the card renders the same regardless
 * of the host page's root font size.
 */

export const CARD_WIDTH = 500;

const SSN_TITLE = 'Shri Sant Nagar, Shri Aagya-Patra';
const PRIMARY_COLOR = '#038b00';

const cardTheme = createTheme({
	palette: {
		primary: {
			main: PRIMARY_COLOR,
			contrastText: '#ffffff',
		},
	},
	typography: {
		fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
		h5: { fontSize: 24 },
		h6: { fontSize: 20 },
		subtitle1: { fontSize: 19, letterSpacing: 0 },
		body1: { fontSize: 19, letterSpacing: 0 },
		body2: { fontSize: 17, letterSpacing: 0 },
	},
});

const useStyles = makeStyles(() => ({
	card: {
		position: 'relative',
		margin: 0,
		width: CARD_WIDTH,

		'& .MuiCardContent-root': {
			paddingBottom: 12,
		},

		'& .MuiListItem-root': {
			paddingLeft: 0,
			paddingRight: 8,
		},
	},
	plainAppBar: {
		borderTop: `8px solid ${PRIMARY_COLOR}`,
		backgroundColor: 'white',
		color: 'inherit',

		'& .MuiToolbar-root': {
			minHeight: 56,
			paddingTop: 0,
		},
	},
	avatar: {
		color: '#fff',
		backgroundColor: blueGrey[300],
	},
	chip: {
		float: 'right',
		padding: 4,

		'& .MuiChip-label': {
			overflow: 'inherit',
			textOverflow: 'inherit',
		},
	},
	inline: {
		display: 'inline',
		fontWeight: 700,
	},
}));

interface Props {
	cardNo: string | number;
	name: string;
	reference?: string;
	peopleCount: number;
	arrival: string;
	qrCode: string;
}

export function buildCardQRCode(cardNo: string | number, peopleCount: number) {
	return QRCode.toDataURL(`https://www.shrianandpur.org/sandesh/?no=${cardNo}&count=${peopleCount}`, { margin: 2 });
}

function CardBody({ cardNo, name, reference, peopleCount, arrival, qrCode }: Props) {
	let classes = useStyles();

	return (
		<Card className={classes.card}>
			<AppBar elevation={4} className={classes.plainAppBar} style={{ position: 'relative' }}>
				<Toolbar style={{ alignSelf: 'center' }}>
					<Typography variant="h6" noWrap>
						{SSN_TITLE}
					</Typography>
				</Toolbar>
			</AppBar>
			<CardContent>
				<Grid container spacing={2}>
					<Grid item xs={3}>
						{qrCode ? <img src={qrCode} width="100%" alt="" /> : null}
					</Grid>
					<Grid item xs={9}>
						<Typography gutterBottom variant="h5" component="h2">
							{name}
						</Typography>
						{reference ? (
							<Typography style={{ marginTop: -10, marginBottom: 8 }} variant="subtitle1" color="textSecondary">
								Through — {reference}
							</Typography>
						) : null}
					</Grid>
				</Grid>
				<Divider />
				<List>
					<ListItem alignItems="flex-start">
						<ListItemAvatar>
							<Avatar className={classes.avatar}>
								<RecentActorsIcon />
							</Avatar>
						</ListItemAvatar>
						<ListItemText
							primary={
								<>
									Card ID <Chip className={classes.chip} style={{ fontSize: 22 }} variant="outlined" label={cardNo} />
								</>
							}
							secondary=" — Show this number at entry gate"
						/>
					</ListItem>
					<Divider variant="inset" component="li" />
					<ListItem alignItems="flex-start">
						<ListItemAvatar>
							<Avatar className={classes.avatar}>
								<EventAvailableIcon />
							</Avatar>
						</ListItemAvatar>
						<ListItemText
							primary={
								<>
									Arrival{' '}
									<Typography component="span" variant="body2" className={classes.inline} style={{ float: 'right' }} color="textPrimary">
										{arrival}
									</Typography>
								</>
							}
							secondary=" — Permitted date of arrival"
						/>
					</ListItem>
					<Divider variant="inset" component="li" />
					<ListItem alignItems="flex-start">
						<ListItemAvatar>
							<Avatar className={classes.avatar}>
								<FaceIcon />
							</Avatar>
						</ListItemAvatar>
						<ListItemText
							primary={
								<>
									Visitors{' '}
									<Chip
										className={classes.chip}
										style={{ fontSize: 16 }}
										variant="outlined"
										label={`${peopleCount} / ${numberToText(peopleCount)}`}
									/>
								</>
							}
							secondary="— Allowed number of visitors"
						/>
					</ListItem>
				</List>
				<Divider />
				<Typography style={{ marginTop: 12 }} variant="body2" color="textSecondary" component="p">
					Note: Accommodation at Shri Sant Nagar is only for one night.
				</Typography>
			</CardContent>
		</Card>
	);
}

export function AnumatiCardImage(props: Props) {
	return (
		<ThemeProvider theme={cardTheme}>
			<CardBody {...props} />
		</ThemeProvider>
	);
}

export default AnumatiCardImage;
